/**
 * CommentTimeline — Phase B + Fashion Uplift polish.
 *
 * Polymorphic comment + activity timeline shared by Purchase Orders, Product
 * Development and Shipments.
 *
 * Render order: oldest → newest. The composer is pinned below with `@mention`
 * autocomplete. Each entry renders by `kind` (comment vs system event) with
 * per-action icons. Author avatars are rendered when `author_avatar_url` is
 * non-null on the timeline payload; otherwise fall back to an initials chip.
 * The rail's vertical line is masked behind the avatar via `ring-4
 * ring-background` (the GitHub trick) so the avatar reads as a solid disc.
 */
import { useMemo, useRef, useState } from 'react';
import {
  Loader2,
  MessageSquare,
  Pencil,
  Send,
  Trash2,
  X,
  Check,
  Activity,
  GitBranch,
  ArrowRightLeft,
  Paperclip,
  Link2,
  Reply,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { useTimeline, useCommentMutations } from '../hooks/useTimeline';
import { useMentionableUsers } from '@/features/product-dev/hooks/useProductDev';

type EntityType = 'purchase_order' | 'product_dev' | 'shipment';
type FilterKind = 'all' | 'comments' | 'events';

interface Props {
  entityType: EntityType;
  entityId: string;
  /** Optional title override — defaults to "Activity". */
  title?: string;
  /**
   * Partner-portal mode (entityType must be 'product_dev'): the caller is a
   * supplier org viewing a buyer-owned shared record, so the own-org comments
   * router would return nothing. Reads/writes route through the junction-gated
   * `supplierPortal` procedures instead, @mentions list buyer-org members, and
   * edit/delete are hidden (no supplier-side procedures for them).
   */
  partner?: boolean;
}

const formatRelative = (iso: string): string => {
  const date = new Date(iso);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
};

const initials = (name: string | null, fallback = '?'): string => {
  if (!name) return fallback;
  const parts = name.trim().split(/\s+/).filter((p) => p.length > 0);
  if (parts.length === 0) return fallback;
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase() || fallback;
};

// How many rows to show before "Show earlier activity" collapses the rest.
const INITIAL_VISIBLE = 6;
// A run of this many consecutive same-kind events collapses into one summary
// row. Item-scoped runs (e.g. per-line price overrides) collapse from 2.
const GROUP_MIN = 3;

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Normalise an event into a grouping key + a human label. Per-item events
 * (e.g. "Set price override to 5.12 on item <uuid>") differ only by the item
 * id, so stripping UUIDs + the "on item" tail collapses a whole bulk action
 * into one key. `itemScoped` drives the "on N items" vs "(N×)" summary wording.
 */
const normalizeEvent = (
  description: string | null,
  action: string,
): { key: string; label: string; itemScoped: boolean } => {
  const raw = (description ?? action.replace(/_/g, ' ')).trim();
  const itemScoped = /\bon item\b/i.test(raw);
  const label = raw
    .replace(UUID_RE, '')
    .replace(/\bon item\b\s*/i, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return { key: `${action}::${label.toLowerCase()}`, label, itemScoped };
};

/** Icon variation by activity action. Unknown actions fall through to a
 *  generic dot — keeps the timeline stable when the backend adds new
 *  event kinds before the frontend learns about them. */
const eventIcon = (action: string) => {
  switch (action) {
    case 'stage_changed':
      return GitBranch;
    case 'status_changed':
      return ArrowRightLeft;
    case 'file_uploaded':
      return Paperclip;
    default:
      return Activity;
  }
};

const Avatar = ({
  name,
  avatarUrl,
  size = 'md',
  className,
}: {
  name: string | null;
  avatarUrl: string | null;
  size?: 'sm' | 'md';
  className?: string;
}) => (
  <span
    className={cn(
      'inline-flex rounded-full overflow-hidden shrink-0',
      size === 'sm' ? 'h-6 w-6 text-[10px]' : 'h-8 w-8 text-xs',
      className,
    )}
  >
    {avatarUrl ? (
      <img src={avatarUrl} alt={name ?? 'avatar'} className="h-full w-full object-cover" />
    ) : (
      <span className="h-full w-full flex items-center justify-center bg-threadheads-purple/10 font-semibold text-threadheads-purple">
        {initials(name)}
      </span>
    )}
  </span>
);

const EventIconChip = ({ icon: Icon }: { icon: React.ComponentType<{ className?: string }> }) => (
  <span className="inline-flex h-8 w-8 rounded-full bg-muted items-center justify-center text-muted-foreground shrink-0">
    <Icon className="h-4 w-4" />
  </span>
);

export function CommentTimeline({ entityType, entityId, title = 'Activity', partner = false }: Props) {
  const { user, profile } = useAuth();
  const utils = trpc.useUtils();
  const currentUserName = profile
    ? `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim() || profile.email
    : null;
  // Own-org and partner sources are both instantiated (hooks can't be
  // conditional); `enabled` flags ensure exactly one fetches.
  const ownTimelineQuery = useTimeline(entityType, partner ? undefined : entityId);
  const partnerTimelineQuery = trpc.supplierPortal.getSharedProductDevTimeline.useQuery(
    { productDevId: entityId },
    { enabled: partner, staleTime: 15_000 },
  );
  const timelineQuery = partner ? partnerTimelineQuery : ownTimelineQuery;
  const { addComment, editComment, deleteComment } = useCommentMutations(
    entityType,
    partner ? undefined : entityId,
  );
  const partnerAddComment = trpc.supplierPortal.addProductDevComment.useMutation({
    onSuccess: () => {
      void utils.supplierPortal.getSharedProductDevTimeline.invalidate({ productDevId: entityId });
    },
  });
  // entityId widens the buyer-side picker to the partner org this record is
  // shared with (product_dev only, server-side gated on an active junction).
  const ownMentionsQuery = useMentionableUsers(entityType, !partner, entityId);
  const partnerMentionsQuery = trpc.supplierPortal.listSharedProductDevMentionableUsers.useQuery(
    { productDevId: entityId },
    { enabled: partner, staleTime: 5 * 60_000 },
  );
  const mentionsQuery = partner ? partnerMentionsQuery : ownMentionsQuery;
  const isAddingComment = partner ? partnerAddComment.isPending : addComment.isPending;
  const composerRef = useRef<HTMLTextAreaElement | null>(null);

  const [draft, setDraft] = useState('');
  const [draftMentions, setDraftMentions] = useState<string[]>([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  // Keyboard-highlighted row in the mention picker. The top match is always
  // pre-selected so plain Enter picks it; arrows move when there are several.
  const [mentionIndex, setMentionIndex] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');
  const [replyTarget, setReplyTarget] = useState<{ id: string; authorName: string | null } | null>(null);
  const [filter, setFilter] = useState<FilterKind>('all');
  // Expanded summary-group ids, and whether the older (pre-window) rows are shown.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());
  const [showAllActivity, setShowAllActivity] = useState(false);

  const entries = useMemo(() => timelineQuery.data ?? [], [timelineQuery.data]);
  const visibleEntries = useMemo(() => {
    if (filter === 'comments') return entries.filter((e) => e.kind === 'comment');
    if (filter === 'events') return entries.filter((e) => e.kind === 'event');
    return entries;
  }, [entries, filter]);

  // Collapse runs of consecutive, near-identical events (e.g. a bulk price
  // override that logs one event per line item) into a single expandable
  // summary row. Comments and one-off events pass through unchanged.
  type Entry = (typeof visibleEntries)[number];
  type EventEntry = Extract<Entry, { kind: 'event' }>;
  type CommentEntry = Extract<Entry, { kind: 'comment' }>;
  type Comment = CommentEntry['comment'];
  type Row =
    | { type: 'comment'; entry: CommentEntry; replies: Comment[] }
    | { type: 'event'; event: EventEntry['event'] }
    | {
        type: 'group';
        id: string;
        actor: string | null;
        label: string;
        itemScoped: boolean;
        latestAt: string;
        events: EventEntry['event'][];
      };
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    const comments = visibleEntries
      .filter((entry): entry is CommentEntry => entry.kind === 'comment');
    const visibleCommentIds = new Set(comments.map((entry) => entry.comment.id));
    const commentEntryById = new Map(comments.map((entry) => [entry.comment.id, entry]));
    const repliesByRoot = new Map<string, Comment[]>();
    for (const entry of comments) {
      const parentId = entry.comment.parent_comment_id;
      if (!parentId || !visibleCommentIds.has(parentId)) continue;
      const list = repliesByRoot.get(parentId) ?? [];
      list.push(entry.comment);
      repliesByRoot.set(parentId, list);
    }
    let i = 0;
    while (i < visibleEntries.length) {
      const entry = visibleEntries[i]!;
      if (entry.kind === 'comment') {
        const parentId = entry.comment.parent_comment_id;
        if (!parentId || !visibleCommentIds.has(parentId)) {
          const replies = repliesByRoot.get(entry.comment.id) ?? [];
          // A quiet thread stays at its root's original position. An active
          // thread is emitted when its latest reply is reached below, keeping
          // fresh replies inside the recent-activity window.
          if (replies.length === 0) {
            out.push({ type: 'comment', entry, replies });
          }
        } else {
          const replies = repliesByRoot.get(parentId) ?? [];
          const latestReply = replies[replies.length - 1];
          if (latestReply?.id === entry.comment.id) {
            const rootEntry = commentEntryById.get(parentId);
            if (rootEntry) out.push({ type: 'comment', entry: rootEntry, replies });
          }
        }
        i += 1;
        continue;
      }
      const norm = normalizeEvent(entry.event.description, entry.event.action);
      let j = i + 1;
      while (j < visibleEntries.length) {
        const next = visibleEntries[j]!;
        if (next.kind !== 'event') break;
        const n2 = normalizeEvent(next.event.description, next.event.action);
        if (n2.key !== norm.key || next.event.actor_name !== entry.event.actor_name) break;
        j += 1;
      }
      const run = visibleEntries.slice(i, j) as EventEntry[];
      const shouldGroup =
        run.length >= GROUP_MIN || (norm.itemScoped && run.length >= 2);
      if (shouldGroup) {
        out.push({
          type: 'group',
          id: `g-${run[0]!.event.id}`,
          actor: entry.event.actor_name,
          label: norm.label,
          itemScoped: norm.itemScoped,
          latestAt: run[run.length - 1]!.event.created_at,
          events: run.map((r) => r.event),
        });
      } else {
        for (const r of run) out.push({ type: 'event', event: r.event });
      }
      i = j;
    }
    return out;
  }, [visibleEntries]);

  // Pagination: show the most recent INITIAL_VISIBLE rows (entries are
  // oldest→newest), with a toggle to reveal the earlier history above them.
  const hiddenCount = Math.max(0, rows.length - INITIAL_VISIBLE);
  const visibleRows = showAllActivity || hiddenCount === 0
    ? rows
    : rows.slice(rows.length - INITIAL_VISIBLE);

  const toggleGroup = (id: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderGroup = (row: Extract<Row, { type: 'group' }>) => {
    const isOpen = expandedGroups.has(row.id);
    const Icon = eventIcon(row.events[0]?.action ?? '');
    const count = row.events.length;
    return (
      <li key={row.id} className="py-1">
        <button
          type="button"
          onClick={() => toggleGroup(row.id)}
          className="flex w-full items-center gap-3 rounded-md px-1 py-1.5 text-left hover:bg-muted/40"
          aria-expanded={isOpen}
        >
          <EventIconChip icon={Icon} />
          <span className="min-w-0 flex-1 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{row.actor ?? 'System'}</span>{' '}
            <span>{row.label}</span>{' '}
            <span className="font-medium text-foreground">
              {row.itemScoped ? `on ${count} items` : `(${count}×)`}
            </span>
            <span className="ml-1" title={new Date(row.latestAt).toLocaleString()}>
              · {formatRelative(row.latestAt)}
            </span>
          </span>
          {isOpen ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
        </button>
        {isOpen && (
          <ul className="ml-11 mt-1 max-h-56 space-y-1 overflow-y-auto border-l pl-3">
            {row.events.map((ev) => (
              <li key={`e-${ev.id}`} className="text-xs text-muted-foreground">
                <span>{ev.description ?? ev.action.replace(/_/g, ' ')}</span>
                <span className="ml-1" title={new Date(ev.created_at).toLocaleString()}>
                  · {formatRelative(ev.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  const mentionMatches = useMemo(() => {
    const term = mentionFilter.trim().toLowerCase();
    const all = mentionsQuery.data ?? [];
    // No result cap: a hard slice(0, 5) hid everyone past the first five
    // alphabetical names, so an external factory contact ("Raphael TL",
    // sorting under R) was unreachable by browsing. The dropdown scrolls
    // instead. Matching also covers organization_name so typing the factory
    // name (e.g. "thread lab") surfaces its people.
    if (!term) return all;
    return all.filter(
      (u) =>
        u.display_name.toLowerCase().includes(term) ||
        (u.organization_name ?? '').toLowerCase().includes(term),
    );
  }, [mentionsQuery.data, mentionFilter]);

  const handleSubmit = async () => {
    const body = draft.trim();
    if (!body) return;
    try {
      if (partner) {
        await partnerAddComment.mutateAsync({
          productDevId: entityId,
          body,
          mentions: draftMentions.length > 0 ? draftMentions : undefined,
          parentCommentId: replyTarget?.id,
        });
      } else {
        await addComment.mutateAsync({
          entityType,
          entityId,
          body,
          mentions: draftMentions.length > 0 ? draftMentions : undefined,
          parentCommentId: replyTarget?.id,
        });
      }
      setDraft('');
      setDraftMentions([]);
      setReplyTarget(null);
    } catch (err) {
      toast({
        title: 'Failed to add comment',
        description: err instanceof Error ? err.message : 'Try again.',
        variant: 'destructive',
      });
    }
  };

  const handleEditSave = async (commentId: string) => {
    const body = editingBody.trim();
    if (!body) return;
    try {
      await editComment.mutateAsync({ commentId, body });
      setEditingId(null);
      setEditingBody('');
    } catch (err) {
      toast({
        title: 'Failed to save edit',
        description: err instanceof Error ? err.message : 'Try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await deleteComment.mutateAsync({ commentId });
    } catch (err) {
      toast({
        title: 'Failed to delete comment',
        description: err instanceof Error ? err.message : 'Try again.',
        variant: 'destructive',
      });
    }
  };

  const handleComposerChange = (value: string) => {
    setDraft(value);
    // Track the typed prefix after the last `@` that isn't followed by a
    // space — keeps the picker open while the user types and closes it on
    // selection or non-word characters.
    const caret = composerRef.current?.selectionStart ?? value.length;
    const upToCaret = value.slice(0, caret);
    const at = upToCaret.lastIndexOf('@');
    if (at < 0) {
      setMentionOpen(false);
      return;
    }
    const prefix = upToCaret.slice(at + 1);
    if (/[\s]/.test(prefix) || prefix.length > 30) {
      setMentionOpen(false);
      return;
    }
    setMentionFilter(prefix);
    setMentionIndex(0); // re-anchor on the most relevant match as the filter changes
    setMentionOpen(true);
  };

  const insertMention = (user: { id: string; display_name: string }) => {
    const caret = composerRef.current?.selectionStart ?? draft.length;
    const before = draft.slice(0, caret);
    const after = draft.slice(caret);
    const at = before.lastIndexOf('@');
    if (at < 0) return;
    const next = `${before.slice(0, at)}@${user.display_name} ${after}`;
    setDraft(next);
    setDraftMentions((m) => (m.includes(user.id) ? m : [...m, user.id]));
    setMentionOpen(false);
    setMentionFilter('');
    // Restore caret position just after the inserted handle.
    requestAnimationFrame(() => {
      const t = composerRef.current;
      if (!t) return;
      const pos = at + 1 + user.display_name.length + 1;
      t.focus();
      t.setSelectionRange(pos, pos);
    });
  };

  const copyDeepLink = async (commentId: string) => {
    try {
      const url = `${window.location.href.split('#')[0]}#comment-${commentId}`;
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied' });
    } catch {
      // Clipboard API isn't critical — silent failure is fine.
    }
  };

  const startReply = (comment: Comment) => {
    setReplyTarget({
      id: comment.parent_comment_id ?? comment.id,
      authorName: comment.author_name,
    });
    requestAnimationFrame(() => composerRef.current?.focus());
  };

  const renderComment = (comment: Comment, isReply = false) => {
    const isAuthor = comment.author_id === user?.id;
    const isEditing = editingId === comment.id;
    return (
      <li
        key={`c-${comment.id}`}
        id={`comment-${comment.id}`}
        className={cn(
          'group flex gap-3 rounded-md px-1 py-3 scroll-mt-24 hover:bg-muted/30 transition-colors motion-reduce:transition-none',
          isReply && 'py-2',
        )}
      >
        <Avatar
          name={comment.author_name}
          avatarUrl={comment.author_avatar_url ?? null}
          size={isReply ? 'sm' : 'md'}
          className="mt-0.5"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">
              {comment.author_name ?? 'Unknown user'}
            </span>
            <a
              href={`#comment-${comment.id}`}
              onClick={(event) => {
                event.preventDefault();
                void copyDeepLink(comment.id);
              }}
              className="hover:text-foreground hover:underline"
              title={new Date(comment.created_at).toLocaleString()}
            >
              {formatRelative(comment.created_at)}
            </a>
            {comment.edited_at && <span className="italic">(edited)</span>}
            <span className="ml-auto flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none">
              <button
                type="button"
                onClick={() => startReply(comment)}
                className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                aria-label={`Reply to ${comment.author_name ?? 'comment'}`}
              >
                <Reply className="h-3 w-3" /> Reply
              </button>
              <button
                type="button"
                onClick={() => void copyDeepLink(comment.id)}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Copy link to comment"
              >
                <Link2 className="h-3 w-3" />
              </button>
              {!partner && isAuthor && !isEditing && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(comment.id);
                      setEditingBody(comment.body);
                    }}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Edit comment"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(comment.id)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Delete comment"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </>
              )}
            </span>
          </div>
          {isEditing ? (
            <div className="mt-2 space-y-2">
              <Textarea
                value={editingBody}
                onChange={(event) => setEditingBody(event.target.value)}
                className="min-h-[60px] text-sm"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingId(null);
                    setEditingBody('');
                  }}
                  className="h-7 px-2 text-xs"
                >
                  <X className="mr-1 h-3 w-3" /> Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void handleEditSave(comment.id)}
                  disabled={editComment.isPending || !editingBody.trim()}
                  className="h-7 px-2 text-xs"
                >
                  {editComment.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" />
                  ) : (
                    <><Check className="mr-1 h-3 w-3" /> Save</>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {comment.body}
            </p>
          )}
        </div>
      </li>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            {title}
          </CardTitle>
          <div className="flex gap-1">
            {(['all', 'comments', 'events'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setFilter(k)}
                className={cn(
                  'h-7 px-3 rounded-md text-xs font-medium transition-colors',
                  filter === k
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                )}
              >
                {k === 'all' ? 'All' : k === 'comments' ? 'Comments' : 'Events'}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {timelineQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading timeline…
          </div>
        ) : timelineQuery.error ? (
          <div className="rounded-md border bg-destructive/5 p-3 text-sm text-destructive">
            Failed to load timeline. {timelineQuery.error.message}
          </div>
        ) : visibleEntries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {filter === 'all'
              ? 'No comments or activity yet. Be the first to add a note below.'
              : filter === 'comments'
              ? 'No comments yet.'
              : 'No activity events yet.'}
          </p>
        ) : (
          <>
            {hiddenCount > 0 && !showAllActivity && (
              <button
                type="button"
                onClick={() => setShowAllActivity(true)}
                className="mb-1 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                Show earlier activity ({hiddenCount} more)
              </button>
            )}
          <ol className="space-y-1">
            {visibleRows.map((row) => {
              if (row.type === 'group') {
                return renderGroup(row);
              }
              if (row.type === 'comment') {
                return (
                  <li key={`thread-${row.entry.comment.id}`} className="list-none">
                    <ul>{renderComment(row.entry.comment)}</ul>
                    {row.replies.length > 0 && (
                      <ul className="ml-6 border-l pl-3 sm:ml-11">
                        {row.replies.map((reply) => renderComment(reply, true))}
                      </ul>
                    )}
                  </li>
                );
              }

              const ev = row.event;
              const IconComp = eventIcon(ev.action);
              return (
                <li key={`e-${ev.id}`} className="flex items-center gap-3 py-2 px-1">
                  <EventIconChip icon={IconComp} />
                  <div className="flex-1 min-w-0 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {ev.actor_name ?? 'System'}
                    </span>{' '}
                    <span>{ev.description ?? ev.action.replace(/_/g, ' ')}</span>
                    <span className="ml-1" title={new Date(ev.created_at).toLocaleString()}>
                      · {formatRelative(ev.created_at)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
          </>
        )}

        {/* Composer */}
        <div className="flex gap-3 pt-2 border-t">
          <Avatar name={currentUserName} avatarUrl={null} className="mt-1" />
          <div className="flex-1 min-w-0 space-y-2 relative">
            {replyTarget && (
              <div className="flex items-center justify-between gap-3 rounded-md bg-muted/50 px-3 py-2 text-xs">
                <span className="inline-flex min-w-0 items-center gap-1.5 text-muted-foreground">
                  <Reply className="h-3.5 w-3.5 shrink-0" />
                  Replying to <span className="truncate font-medium text-foreground">{replyTarget.authorName ?? 'comment'}</span>
                </span>
                <button
                  type="button"
                  onClick={() => setReplyTarget(null)}
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label="Cancel reply"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <Textarea
              ref={composerRef}
              placeholder="Add a comment… (type @ to mention)"
              value={draft}
              onChange={(e) => handleComposerChange(e.target.value)}
              onKeyDown={(e) => {
                if (mentionOpen && e.key === 'Escape') {
                  e.preventDefault();
                  setMentionOpen(false);
                  return;
                }
                // While the picker is open it owns Enter/Tab/arrows; the
                // highlighted match is inserted without reaching for the mouse.
                if (mentionOpen && mentionMatches.length > 0) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setMentionIndex((i) => (i + 1) % mentionMatches.length);
                    return;
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setMentionIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
                    return;
                  }
                  if ((e.key === 'Enter' && !e.metaKey && !e.ctrlKey) || e.key === 'Tab') {
                    e.preventDefault();
                    const pick = mentionMatches[mentionIndex] ?? mentionMatches[0];
                    if (pick) insertMention(pick);
                    return;
                  }
                }
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void handleSubmit();
                }
              }}
              className="min-h-[72px] text-sm resize-none"
              disabled={isAddingComment}
            />
            {mentionOpen && mentionMatches.length > 0 && (
              <div className="absolute bottom-full mb-1 left-0 z-20 w-72 rounded-md border bg-popover text-popover-foreground shadow-md p-1 max-h-64 overflow-y-auto">
                {mentionMatches.map((u, idx) => (
                  <button
                    type="button"
                    key={u.id}
                    onClick={() => insertMention(u)}
                    onMouseEnter={() => setMentionIndex(idx)}
                    // Keep the keyboard-highlighted row visible now that the
                    // uncapped list scrolls. Optional call: jsdom (unit tests)
                    // has no scrollIntoView.
                    ref={(el) => {
                      if (idx === mentionIndex) el?.scrollIntoView?.({ block: 'nearest' });
                    }}
                    aria-selected={idx === mentionIndex}
                    className={cn(
                      'w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-muted text-sm',
                      idx === mentionIndex && 'bg-muted',
                    )}
                  >
                    <Avatar
                      name={u.display_name}
                      avatarUrl={u.avatar_url}
                      size="sm"
                    />
                    <span className="flex-1 min-w-0 truncate">{u.display_name}</span>
                    {/* External-org marker. Two orgs routinely share first
                        names, and a mention carries the style name outside the
                        company — so whose Daniel this is must be visible at
                        the moment of choosing, not discoverable afterwards. */}
                    {u.organization_name && (
                      // ring-1 keeps the chip's silhouette on the highlighted
                      // row, whose background is also bg-muted — without it the
                      // external marker disappears on exactly the row Enter
                      // will select. max-w so a long factory name yields before
                      // the person's name, which is the primary information.
                      <span className="shrink-0 max-w-[96px] truncate rounded-sm bg-muted px-1.5 py-px text-[10px] text-muted-foreground ring-1 ring-border">
                        {u.organization_name}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] text-muted-foreground">
                ⌘/Ctrl + Enter to send · type @ to mention
              </p>
              <Button
                type="button"
                size="sm"
                onClick={handleSubmit}
                disabled={!draft.trim() || isAddingComment}
                className="h-8 px-3 text-xs"
              >
                {isAddingComment ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5 mr-1" /> {replyTarget ? 'Reply' : 'Comment'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
